import React from "react";
import {
    ParticipantAssessmentStatus,
    SessionInstrument,
} from "../../../../../../services/types/enterpriseParticipantApiTypes";
import AssessmentStatusDataGridPdf from "./assessmentStatusDataGridPdf";
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Dialog,
    DialogActions,
    DialogContent,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import CclButton from "../../../../../../components/common/cclButtons/cclButton";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import SelfLegendTable from "./assessmentStatusLegendDrawer/selfLegendTable";
import ScorableLegendTable from "./assessmentStatusLegendDrawer/scorableLegendTable";
import RaterLegendTable from "./assessmentStatusLegendDrawer/raterLegendTable";
import CclLoadingButton from "../../../../../../components/common/cclButtons/cclLoadingButton";
import useLogAccessEvent from "../../../../../../hooks/useLogAccessEvent";
import {
    AccessEventRegistrationDetails,
    AccessEventSessionDetails,
} from "../../../../../../services/types/accessEventTypes";

interface AssessmentStatusPdfProps {
    sessionTitle?: string;
    sessionCode: string;
    sessionKey: number;
    instruments: SessionInstrument[];
    gridParticipants: ParticipantAssessmentStatus[];
    open: boolean;
    onClose: () => void;
    startDate: Date | null;
    surveyCompletionDate: Date | null;
    preworkInvitationDate: Date | null;
    reminder1Date: Date | null;
    reminder2Date: Date | null;
    reminder3Date: Date | null;
    showSnackbar: (message: string, severity: "error" | "warning" | "info" | "success") => void;
}

interface InstrumentGroup {
    instruments: SessionInstrument[];
    columnCount: number;
}

const AssessmentStatusPdf: React.FC<AssessmentStatusPdfProps> = (props) => {
    const gridRef = React.useRef<HTMLDivElement>(null);
    const { logEvent } = useLogAccessEvent();

    const [generatingPdf, setGeneratingPdf] = React.useState(false);
    const [instrumentGroups, setInstrumentGroups] = React.useState<InstrumentGroup[]>([]);
    const [dialogWidth, setDialogWidth] = React.useState<number | undefined>(undefined);
    const [showNoteEditor, setShowNoteEditor] = React.useState<boolean>(false);
    const [noteText, setNoteText] = React.useState<string | null>(null);
    const hiddenGridRefs = React.useRef<(HTMLDivElement | null)[]>([]);

    React.useEffect(() => {
        // After instrumentGroups change, measure widths
        setTimeout(() => {
            let maxWidth = 0;
            hiddenGridRefs.current.forEach((ref) => {
                if (ref && ref.offsetWidth > maxWidth) {
                    maxWidth = ref.offsetWidth;
                }
            });
            if (maxWidth > 0) setDialogWidth(maxWidth + 48); // Add padding/margin as needed
        }, 100); // Wait for rendering
    }, [instrumentGroups]);

    React.useEffect(() => {
        function getInstrumentColumnCount(inst: SessionInstrument): number {
            if (inst.hasRaters) {
                return 2 + inst.raters.filter((r) => r.raterTypeId !== 1).length;
            }
            return 1;
        }

        function groupInstrumentsByColumnCount(
            instruments: SessionInstrument[],
            minCols: number,
            maxCols: number
        ): InstrumentGroup[] {
            if (instruments.length === 0) return [];

            const groups: InstrumentGroup[] = [];
            let currentGroup: SessionInstrument[] = [];
            let currentColCount = 0;

            const sortedInstruments = [...instruments].sort((a, b) => {
                if (a.hasRaters && !b.hasRaters) return 1;
                if (!a.hasRaters && b.hasRaters) return -1;
                return a.name.localeCompare(b.name);
            });

            sortedInstruments.forEach((inst) => {
                const instColCount = getInstrumentColumnCount(inst);
                if (currentGroup.length > 0 && currentColCount + instColCount > maxCols) {
                    // If current group is below minCols, allow overflow
                    if (currentColCount < minCols) {
                        currentGroup.push(inst);
                        currentColCount += instColCount;
                    } else {
                        groups.push({
                            instruments: currentGroup,
                            columnCount: currentColCount,
                        });
                        currentGroup = [inst];
                        currentColCount = 1 + instColCount;
                    }
                } else {
                    currentGroup.push(inst);
                    currentColCount += instColCount;
                }
            });
            if (currentGroup.length > 0) {
                groups.push({
                    instruments: currentGroup,
                    columnCount: currentColCount,
                });
            }
            return groups;
        }

        setInstrumentGroups(groupInstrumentsByColumnCount(props.instruments, 8, 13));
    }, [props.instruments]);

    const generatePdf = async () => {
        if (!gridRef.current) return;

        setGeneratingPdf(true);
        // Capture the hidden content as a canvas
        const canvas = await html2canvas(gridRef.current, { scale: 2 });
        const imgData = canvas.toDataURL("image/jpeg", 0.7);

        const pdf = new jsPDF({
            orientation: "landscape", //"portrait",
            unit: "mm",
            format: "a4",
        });

        const margin = 8; // mm
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        // Calculate image properties
        const imgProps = pdf.getImageProperties(imgData);

        // Scale image to fit page width (minus margins)
        const pdfWidth = pageWidth - 2 * margin;
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        // If the image height fits on one page, use your original logic
        if (pdfHeight <= pageHeight - 2 * margin) {
            pdf.addImage(imgData, "JPEG", margin, margin, pdfWidth, pdfHeight);
        } else {
            // Multi-page logic
            // Calculate the height of one page in pixels at the canvas scale
            const pageHeightPx = ((pageHeight - 2 * margin) * imgProps.height) / pdfHeight;

            let position = 0;
            let pageNum = 0;
            while (position < imgProps.height) {
                // Create a canvas for the current page slice
                const pageCanvas = document.createElement("canvas");
                pageCanvas.width = imgProps.width;
                pageCanvas.height = Math.min(pageHeightPx, imgProps.height - position);

                const ctx = pageCanvas.getContext("2d");
                if (ctx) {
                    ctx.drawImage(
                        canvas,
                        0,
                        position,
                        imgProps.width,
                        pageCanvas.height,
                        0,
                        0,
                        imgProps.width,
                        pageCanvas.height
                    );
                }

                const pageImgData = pageCanvas.toDataURL("image/jpeg", 0.7);
                if (pageNum > 0) pdf.addPage();
                pdf.addImage(
                    pageImgData,
                    "JPEG",
                    margin,
                    margin,
                    pdfWidth,
                    (pageCanvas.height * pdfWidth) / imgProps.width
                );
                position += pageHeightPx;
                pageNum++;
            }
        }

        const date = new Date();
        const pad = (n: number) => n.toString().padStart(2, "0");
        const MM = pad(date.getUTCMonth() + 1);
        const DD = pad(date.getUTCDate());
        const YYYY = date.getUTCFullYear();
        const HH = pad(date.getUTCHours());
        const mm = pad(date.getUTCMinutes());
        const SS = pad(date.getUTCSeconds());
        const timestamp = `${MM}${DD}${YYYY}-${HH}${mm}${SS}`;
        const filename = props.sessionCode
            ? `${props.sessionCode}-Assessment-Status-${timestamp}.pdf`
            : `${props.sessionKey}-Assessment-Status-${timestamp}.pdf`;
        pdf.save(filename);

        const eventData =
            props.gridParticipants.length === 1
                ? ({
                      esiKey: props.gridParticipants[0].esiKey.toString(),
                      imKey: props.gridParticipants[0].imKey.toString(),
                      email: props.gridParticipants[0].email,
                  } as AccessEventRegistrationDetails)
                : ({
                      sessionID: props.sessionKey.toString() ?? "",
                      projectCode: props.sessionCode ?? "",
                  } as AccessEventSessionDetails);

        logEvent("AssessmentStatusExported", eventData);

        setGeneratingPdf(false);
        props.showSnackbar("Assessment Status Exported", "success");
    };

    function formatFullDateLocal(date: Date): string {
        const dateStr = date.toLocaleDateString("en-US", {
            weekday: "long",
            month: "short",
            day: "2-digit",
            year: "numeric",
        });
        const timeStr = date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
            timeZoneName: "short",
        });
        // Remove comma between date and time
        return `${dateStr} ${timeStr}`;
    }

    const updateNotesField = () => {
        if (showNoteEditor && (noteText === null || noteText.trim().length === 0)) {
            setNoteText(null);
        }
        setShowNoteEditor(!showNoteEditor);
    };

    const pdfTitle =
        props.sessionCode != null && props.sessionCode !== ""
            ? `${props.sessionCode}: ${props.sessionTitle}`
            : props.sessionTitle;

    return (
        <Dialog
            open={props.open}
            maxWidth={false}
            fullWidth
            onClose={props.onClose}
            PaperProps={{ style: { width: dialogWidth ? dialogWidth : "auto" } }}
        >
            <DialogContent>
                <Stack spacing={6} padding={3}>
                    <Box>
                        <Stack direction={"row"} justifyContent={"space-between"}>
                            <Stack direction={"column"} width={0.667}>
                                <Typography
                                    variant="h4"
                                    gutterBottom
                                    sx={{ fontStyle: "italic", fontWeight: "bold" }}
                                >
                                    CCL Access Assessment Status Report
                                </Typography>
                                <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
                                    {pdfTitle}
                                </Typography>
                                <Box
                                    sx={{
                                        position: "relative",
                                        border: "1px solid #1976d2",
                                        borderRadius: 2,
                                        padding: 2,
                                        marginY: 2,
                                        backgroundColor: "#f9f9f9",
                                        maxWidth: 800, // reduce overall box width
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            position: "absolute",
                                            top: 0,
                                            left: "7%",
                                            transform: "translate(-50%, -50%)",
                                            backgroundColor: "#f9f9f9",
                                            px: 2,
                                            fontWeight: "bold",
                                        }}
                                    >
                                        Key Dates
                                    </Typography>
                                    <Stack direction="row" spacing={2} marginTop={2}>
                                        {/* Column 1: Labels */}
                                        <Stack flex={0.5} alignItems="flex-end" spacing={2}>
                                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                                Program Start:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                                Pre-work invitation:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                                Survey Completion:
                                            </Typography>
                                        </Stack>
                                        {/* Column 2: Dates */}
                                        <Stack flex={1} alignItems="flex-start" spacing={2}>
                                            <Typography variant="body2">
                                                {props.startDate
                                                    ? new Date(props.startDate).toLocaleDateString(
                                                          "en-US",
                                                          {
                                                              weekday: "long",
                                                              month: "short",
                                                              day: "2-digit",
                                                              year: "numeric",
                                                          }
                                                      )
                                                    : "\u00A0"}
                                            </Typography>
                                            <Typography variant="body2">
                                                {props.preworkInvitationDate
                                                    ? new Date(
                                                          props.preworkInvitationDate
                                                      ).toLocaleDateString("en-US", {
                                                          weekday: "long",
                                                          month: "short",
                                                          day: "2-digit",
                                                          year: "numeric",
                                                      })
                                                    : "\u00A0"}
                                            </Typography>
                                            <Typography variant="body2">
                                                {props.surveyCompletionDate
                                                    ? new Date(
                                                          props.surveyCompletionDate
                                                      ).toLocaleDateString("en-US", {
                                                          weekday: "long",
                                                          month: "short",
                                                          day: "2-digit",
                                                          year: "numeric",
                                                      })
                                                    : "\u00A0"}
                                            </Typography>
                                        </Stack>
                                        {/* Column 3: Labels */}
                                        <Stack flex={0.5} alignItems="flex-end" spacing={2}>
                                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                                Reminder 1:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                                Reminder 2:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                                Reminder 3:
                                            </Typography>
                                        </Stack>
                                        {/* Column 4: Dates */}
                                        <Stack flex={1} alignItems="flex-start" spacing={2}>
                                            <Typography variant="body2">
                                                {props.reminder1Date
                                                    ? new Date(
                                                          props.reminder1Date
                                                      ).toLocaleDateString("en-US", {
                                                          weekday: "long",
                                                          month: "short",
                                                          day: "2-digit",
                                                          year: "numeric",
                                                      })
                                                    : "\u00A0"}
                                            </Typography>
                                            <Typography variant="body2">
                                                {props.reminder2Date
                                                    ? new Date(
                                                          props.reminder2Date
                                                      ).toLocaleDateString("en-US", {
                                                          weekday: "long",
                                                          month: "short",
                                                          day: "2-digit",
                                                          year: "numeric",
                                                      })
                                                    : "\u00A0"}
                                            </Typography>
                                            <Typography variant="body2">
                                                {props.reminder3Date
                                                    ? new Date(
                                                          props.reminder3Date
                                                      ).toLocaleDateString("en-US", {
                                                          weekday: "long",
                                                          month: "short",
                                                          day: "2-digit",
                                                          year: "numeric",
                                                      })
                                                    : "\u00A0"}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Box>
                                <Typography variant="body1" gutterBottom>
                                    This report reflects all assessment materials received through{" "}
                                    {formatFullDateLocal(new Date())}
                                </Typography>
                            </Stack>
                            <Box width={0.333}>
                                <img
                                    src="/media/logo/Access_Logo_Color.png"
                                    alt="CCL logo - small, color"
                                    width={"155px"}
                                    height={"100px"}
                                    style={{ float: "right" }}
                                />
                            </Box>
                        </Stack>
                    </Box>
                    {showNoteEditor ? (
                        <TextField
                            id="notes-input"
                            label="Notes"
                            multiline
                            rows={4}
                            defaultValue={""}
                            value={noteText ?? ""}
                            onChange={(e) => setNoteText(e.target.value)}
                        />
                    ) : null}
                    <Box>
                        {" "}
                        {instrumentGroups.map((group, idx) => (
                            <Box key={idx} width={1} sx={{ pt: 3 }}>
                                <AssessmentStatusDataGridPdf
                                    instruments={group.instruments}
                                    participants={props.gridParticipants}
                                    sessionKey={props.sessionKey}
                                    sessionCode={props.sessionCode}
                                />
                            </Box>
                        ))}
                    </Box>
                    <Card sx={{ pt: 3 }}>
                        <CardHeader
                            title="Legend"
                            titleTypographyProps={{ sx: { textDecoration: "underline" } }}
                        />
                        <CardContent>
                            <Stack direction={"row"} spacing={3}>
                                <Box flex={1}>
                                    <SelfLegendTable />
                                </Box>
                                <Box flex={1}>
                                    <ScorableLegendTable />
                                </Box>
                                <Box flex={1}>
                                    <RaterLegendTable />
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Stack>
            </DialogContent>
            {/* Hidden div for PDF generation */}
            <div style={{ position: "absolute", left: -10000, top: -10000 }} ref={gridRef}>
                <Stack spacing={6} padding={3}>
                    <Box>
                        <Stack direction={"row"} justifyContent={"space-between"}>
                            <Stack direction={"column"} width={0.667}>
                                <Typography
                                    variant="h4"
                                    gutterBottom
                                    sx={{ fontStyle: "italic", fontWeight: "bold" }}
                                >
                                    CCL Access Assessment Status Report
                                </Typography>
                                <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
                                    {pdfTitle}
                                </Typography>
                                <Box
                                    sx={{
                                        position: "relative",
                                        border: "1px solid #1976d2",
                                        borderRadius: 2,
                                        padding: 2,
                                        marginY: 2,
                                        backgroundColor: "#f9f9f9",
                                        maxWidth: 800, // reduce overall box width
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            position: "absolute",
                                            top: 0,
                                            left: "7%",
                                            transform: "translate(-50%, -50%)",
                                            backgroundColor: "#f9f9f9",
                                            px: 2,
                                            fontWeight: "bold",
                                        }}
                                    >
                                        Key Dates
                                    </Typography>
                                    <Stack direction="row" spacing={2} marginTop={2}>
                                        {/* Column 1: Labels */}
                                        <Stack flex={0.5} alignItems="flex-end" spacing={2}>
                                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                                Program Start:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                                Pre-work invitation:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                                Survey Completion:
                                            </Typography>
                                        </Stack>
                                        {/* Column 2: Dates */}
                                        <Stack flex={1} alignItems="flex-start" spacing={2}>
                                            <Typography variant="body2">
                                                {props.startDate
                                                    ? new Date(props.startDate).toLocaleDateString(
                                                          "en-US",
                                                          {
                                                              weekday: "long",
                                                              month: "short",
                                                              day: "2-digit",
                                                              year: "numeric",
                                                          }
                                                      )
                                                    : "\u00A0"}
                                            </Typography>
                                            <Typography variant="body2">
                                                {props.preworkInvitationDate
                                                    ? new Date(
                                                          props.preworkInvitationDate
                                                      ).toLocaleDateString("en-US", {
                                                          weekday: "long",
                                                          month: "short",
                                                          day: "2-digit",
                                                          year: "numeric",
                                                      })
                                                    : "\u00A0"}
                                            </Typography>
                                            <Typography variant="body2">
                                                {props.surveyCompletionDate
                                                    ? new Date(
                                                          props.surveyCompletionDate
                                                      ).toLocaleDateString("en-US", {
                                                          weekday: "long",
                                                          month: "short",
                                                          day: "2-digit",
                                                          year: "numeric",
                                                      })
                                                    : "\u00A0"}
                                            </Typography>
                                        </Stack>
                                        {/* Column 3: Labels */}
                                        <Stack flex={0.5} alignItems="flex-end" spacing={2}>
                                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                                Reminder 1:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                                Reminder 2:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                                Reminder 3:
                                            </Typography>
                                        </Stack>
                                        {/* Column 4: Dates */}
                                        <Stack flex={1} alignItems="flex-start" spacing={2}>
                                            <Typography variant="body2">
                                                {props.reminder1Date
                                                    ? new Date(
                                                          props.reminder1Date
                                                      ).toLocaleDateString("en-US", {
                                                          weekday: "long",
                                                          month: "short",
                                                          day: "2-digit",
                                                          year: "numeric",
                                                      })
                                                    : "\u00A0"}
                                            </Typography>
                                            <Typography variant="body2">
                                                {props.reminder2Date
                                                    ? new Date(
                                                          props.reminder2Date
                                                      ).toLocaleDateString("en-US", {
                                                          weekday: "long",
                                                          month: "short",
                                                          day: "2-digit",
                                                          year: "numeric",
                                                      })
                                                    : "\u00A0"}
                                            </Typography>
                                            <Typography variant="body2">
                                                {props.reminder3Date
                                                    ? new Date(
                                                          props.reminder3Date
                                                      ).toLocaleDateString("en-US", {
                                                          weekday: "long",
                                                          month: "short",
                                                          day: "2-digit",
                                                          year: "numeric",
                                                      })
                                                    : "\u00A0"}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Box>
                                <Typography variant="body1" gutterBottom>
                                    This report reflects all assessment materials received through{" "}
                                    {formatFullDateLocal(new Date())}
                                </Typography>
                            </Stack>
                            <Box width={0.333}>
                                <img
                                    src="/media/logo/Access_Logo_Color.png"
                                    alt="CCL logo - small, color"
                                    width={"155px"}
                                    height={"100px"}
                                    style={{ float: "right" }}
                                />
                            </Box>
                        </Stack>
                    </Box>
                    {noteText !== null && noteText.trim().length > 0 ? (
                        <Box sx={{ border: "1px solid #ccc", padding: 2, borderRadius: 1 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Notes: {noteText}
                            </Typography>
                        </Box>
                    ) : null}
                    <Box>
                        {" "}
                        {instrumentGroups.map((group, idx) => (
                            <Box key={idx} width={"auto"} sx={{ pt: 3 }}>
                                <AssessmentStatusDataGridPdf
                                    instruments={group.instruments}
                                    participants={props.gridParticipants}
                                    sessionKey={props.sessionKey}
                                    sessionCode={props.sessionCode}
                                />
                            </Box>
                        ))}
                    </Box>
                    <Card sx={{ pt: 3 }}>
                        <CardHeader
                            title="Legend"
                            titleTypographyProps={{ sx: { textDecoration: "underline" } }}
                        />
                        <CardContent>
                            <Stack direction={"row"} spacing={3}>
                                <Box flex={1}>
                                    <SelfLegendTable />
                                </Box>
                                <Box flex={1}>
                                    <ScorableLegendTable />
                                </Box>
                                <Box flex={1}>
                                    <RaterLegendTable />
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Stack>
            </div>
            <DialogActions>
                <CclButton onClick={updateNotesField} mode={"secondary"}>
                    {showNoteEditor ? "Hide Notes" : "Add Notes"}
                </CclButton>
                <CclLoadingButton
                    onClick={async () => {
                        await generatePdf();
                    }}
                    mode={"primary"}
                    disabled={instrumentGroups.length <= 0}
                    loading={generatingPdf}
                >
                    Export To PDF
                </CclLoadingButton>
                <CclButton onClick={props.onClose} mode={"primary"}>
                    Close
                </CclButton>
            </DialogActions>
        </Dialog>
    );
};

export default AssessmentStatusPdf;
